import React, { FC, useCallback, useRef } from 'react';
import styled from 'styled-components';

import { focusShadow } from '#/components/styles';
import { noop, remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { FormMarkdown } from '../../Form.styled';
import { getErrorMessagesIds } from '../../Form.utils';
import { Container } from '../FormInput.styled';
import { CommonInputProps } from '../FormInput.types';
import { ErrorMessage } from './ErrorMessage';

interface InputRadioProps extends CommonInputProps {
  inputValue?: string;
  label?: string;
  values: { key: string; value: string }[];
}

interface RadioProps {
  $touched?: boolean;
  $hasError?: boolean;
  $checked?: boolean;
}

const RadioContainer = styled.div<RadioProps>`
  display: flex;
  align-items: center;
  gap: ${remify(8)};
  cursor: pointer;

  /* SVG focus styles */
  & svg {
    outline: none;

    &:focus-visible {
      ${focusShadow}
    }
  }

  /* State styles */
  ${({ $checked }) => `
    &:hover svg path {
      fill: var(${$checked ? '--selectionControlsBackgroundHover' : '--pageParagraphForegroundSubtle'});
    }
  `}

  /* Error state styling */
  ${({ $hasError, $touched }) =>
    $hasError &&
    $touched &&
    `
    svg path {
      fill: var(--inputValidation);
    }

    &:hover svg path {
      fill: var(--inputValidationHover);
    }
  `}
`;

const RadioGroup = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: ${remify(12)};
  border: none;
  margin: 0;
  padding: 0;
`;

const StyledLabel = styled.label`
  flex: 1;
  cursor: pointer;
  user-select: none;

  & * {
    font: var(--fontStyleFootnote);
  }
`;

const RadioWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  input:focus-visible + svg {
    ${focusShadow}
  }
`;

export const InputRadio: FC<InputRadioProps> = ({
  dataKey,
  errorMessages,
  handleBlur = noop,
  inputValue,
  label,
  required,
  touched,
  updateErrorMessages = noop,
  updateInputValue = noop,
  values,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasError = !!errorMessages?.length;

  const validate = () => {
    const validationMessage = inputRefs.current.find(
      (ref) => ref?.validationMessage
    )?.validationMessage;
    updateErrorMessages(dataKey, validationMessage);
  };

  const handleChange = (value: string) => {
    updateInputValue(dataKey, {
      touched: true,
      value,
    });
    validate();
  };

  const handleRadioClick = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
    ) => {
      const index = Number(e.currentTarget.dataset.index);
      inputRefs.current[index]?.click();
    },
    []
  );

  const handleOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleRadioClick(e);
      }
    },
    [handleRadioClick]
  );

  const errorMessageIds = hasError
    ? getErrorMessagesIds({ dataKey, errorMessages })
    : undefined;

  return (
    <Container>
      <RadioGroup>
        <legend className="visually-hidden">{tb(label)}</legend>
        {values.map((option, index) => {
          const isChecked = inputValue === option.key;
          const optionId = `${dataKey}-${option.key}`;

          return (
            <RadioContainer
              $checked={isChecked}
              $hasError={hasError}
              $touched={touched}
              key={option.key}
            >
              <RadioWrapper
                data-index={index}
                data-testid="radio-wrapper"
                onClick={handleRadioClick}
                onKeyDown={handleOnKeyDown}
              >
                <input
                  aria-describedby={errorMessageIds}
                  aria-labelledby={`${optionId}-label`}
                  checked={isChecked}
                  className="visually-hidden"
                  data-testid="input-radio"
                  id={optionId}
                  name={dataKey}
                  onBlur={() => {
                    handleBlur();
                    validate();
                  }}
                  onChange={() => handleChange(option.key)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  required={required}
                  type="radio"
                  value={option.key}
                />
                {isChecked ? <RadioChecked /> : <RadioUnchecked />}
              </RadioWrapper>
              <StyledLabel htmlFor={optionId} id={`${optionId}-label`}>
                <FormMarkdown>{tb(option.value)}</FormMarkdown>
              </StyledLabel>
            </RadioContainer>
          );
        })}
      </RadioGroup>
      {errorMessages?.map((errorMessage, index) => (
        <ErrorMessage
          aria-live="polite"
          id={`${dataKey}-error-message-${index}`}
          key={`${dataKey}-error-message-${index}`}
        >
          {errorMessage}
        </ErrorMessage>
      ))}
    </Container>
  );
};

export const RadioChecked = () => (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="https://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C19.9624 7.5974 16.4026 4.03762 12 4ZM12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C21.9826 6.48439 17.5156 2.01743 12 2ZM12 5C8.13401 5 5 8.13401 5 12C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12C18.9878 8.13907 15.8609 5.0122 12 5Z"
      fill="var(--selectionControlsBackground)"
      fillRule="evenodd"
    />
  </svg>
);

export const RadioUnchecked = () => (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="https://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C19.9624 7.5974 16.4026 4.03762 12 4ZM12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C21.9826 6.48439 17.5156 2.01743 12 2Z"
      fill="var(--pageParagraphForeground)"
      fillRule="evenodd"
    />
  </svg>
);
